"use client";

import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useImageUpload,
  useQuotaStatus,
  useWatermarkTemplates,
  useCreateWatermarkTemplate,
  useUpdateWatermarkTemplate,
} from "@/lib/hooks";
import type { VideoFormat, FormatImages } from "@/lib/hooks/useImageUpload";
import type { FrontendEffect } from "@/api/types";
import { trackVideoCreation, trackImageUpload } from "@/lib/analytics";
import { Loader2, X, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import placeholderImage from "@/assets/images/hero_1.webp";
import portraitPlaceholderImage from "@/assets/images/hero_2.webp";

// Extracted components and utils
import {
  MIN_IMAGES,
  MAX_IMAGES,
  INITIAL_SLOTS,
  getContainerAlignment,
  resizeImageFile,
  BRANDING_PLACEHOLDER,
  type WatermarkSettings,
  type VideoImage,
} from "./video-form-utils";
import { WatermarkPreview } from "./WatermarkPreview";
import { GeneralInfoStep } from "./GeneralInfoStep";
import { MediaUploadStep } from "./MediaUploadStep";
import { WatermarkStep } from "./WatermarkStep";

interface CreateVideoFormProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onSubmit"
> {
  onFormSubmit?: (data: {
    name: string;
    formats: VideoFormat[];
    imagesByFormat: FormatImages;
    watermarkUrl?: string | null;
    watermarkSettingsByFormat: Record<VideoFormat, WatermarkSettings>;
  }) => void;
  isSubmitting?: boolean;
}

function CreateVideoForm({
  onFormSubmit,
  isSubmitting = false,
  className,
  ...props
}: CreateVideoFormProps) {
  const [name, setName] = useState("");
  const [formats, setFormats] = useState<VideoFormat[]>(["16:9"]);
  const DEFAULT_WATERMARK_SETTINGS: Record<VideoFormat, WatermarkSettings> = {
    "16:9": {
      logoEnabled: false,
      introEnabled: true,
      opacity: 100,
      alignment: "bottom-right",
      padding: 0,
      size: 10,
      introTitle: "<h2>EDIT TITLE HERE</h2><p>Edit body here</p>",
      introStyle: "cinematic",
      introAnimation: "fade",
      introBgColor: "#000000",
      introBgOpacity: 75,
      introTextColor: "#FFFFFF",
      introFont: "",
      introAlignment: "center",
      contactCard: "",
    },
    "9:16": {
      logoEnabled: false,
      introEnabled: true,
      opacity: 100,
      alignment: "bottom-right",
      padding: 0,
      size: 10,
      introTitle: "<h3>EDIT TITLE HERE</h3><p>Edit body here</p>",
      introStyle: "cinematic",
      introAnimation: "fade",
      introBgColor: "#000000",
      introBgOpacity: 75,
      introTextColor: "#FFFFFF",
      introFont: "",
      introAlignment: "center",
      contactCard: "",
    },
  };

  const [watermarkUrl, setWatermarkUrl] = useState<string | null>(null);
  const [watermarkPreview, setWatermarkPreview] = useState<string | null>(null);
  const [watermarkSettingsByFormat, setWatermarkSettingsByFormat] = useState<
    Record<VideoFormat, WatermarkSettings>
  >(DEFAULT_WATERMARK_SETTINGS);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [watermarkChoice, setWatermarkChoice] = useState<"yes" | "no" | null>(
    null,
  );
  const [useSplitFormat, setUseSplitFormat] = useState(false);
  const [, setActiveId] = useState<string | null>(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] =
    useState<boolean>(false);
  const { data: quota } = useQuotaStatus();
  const { data: templatesData } = useWatermarkTemplates();
  const templates = templatesData?.templates || [];
  const { t } = useTranslation();

  const [pendingFormats, setPendingFormats] = useState<VideoFormat[] | null>(
    null,
  );
  const [isSplitToggleDialogOpen, setIsSplitToggleDialogOpen] = useState(false);
  const [isSaveTemplateDialogOpen, setIsSaveTemplateDialogOpen] =
    useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const createTemplateMutation = useCreateWatermarkTemplate();
  const updateTemplateMutation = useUpdateWatermarkTemplate();
  const [submitPromptType, setSubmitPromptType] = useState<"update_template" | "new_template" | null>(null);
  const captureRefs = useRef<Record<string, { captureIntroClip: () => Promise<string[]> } | null>>({});
  const lastSubmittedSettingsRef = useRef<Record<VideoFormat, WatermarkSettings> | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturePhase, setCapturePhase] = useState<"processing" | null>(null);
  const [captureProgress, setCaptureProgress] = useState(0);
  const [isTemplateSavingDialogVisible, setIsTemplateSavingDialogVisible] = useState(false);

  const handleCapturePhaseChange = useCallback((phase: "processing" | null, progress: number) => {
    setCapturePhase(phase);
    setCaptureProgress(progress);
  }, []);
  const [activeBrandingFormat, setActiveBrandingFormat] = useState<VideoFormat>(formats[0] || "16:9");

  // Ensure activeBrandingFormat is always valid when formats change
  useEffect(() => {
    if (!formats.includes(activeBrandingFormat) && formats.length > 0) {
      setActiveBrandingFormat(formats[0]);
    }
  }, [formats, activeBrandingFormat]);

  const hasTemplateModifications = useMemo(() => {
    const activeTemplate = templates.find((t) => t.id === activeTemplateId);
    if (!activeTemplate) return false;

    const getRelevant = (settings: any) => ({
      logoEnabled: settings.logoEnabled,
      introEnabled: settings.introEnabled,
      opacity: settings.opacity,
      alignment: settings.alignment,
      padding: settings.padding,
      size: settings.size,
      introTitle: settings.introTitle,
      introStyle: settings.introStyle,
      introAnimation: settings.introAnimation,
      introBgColor: settings.introBgColor,
      introBgOpacity: settings.introBgOpacity,
      introTextColor: settings.introTextColor,
      introFont: settings.introFont,
      introAlignment: settings.introAlignment,
      contactCard: settings.contactCard,
    });

    const isLandscapeDiff = JSON.stringify(getRelevant(watermarkSettingsByFormat["16:9"])) !== JSON.stringify(getRelevant(activeTemplate.settingsLandscape));
    const isPortraitDiff = JSON.stringify(getRelevant(watermarkSettingsByFormat["9:16"])) !== JSON.stringify(getRelevant(activeTemplate.settingsPortrait));
    
    return isLandscapeDiff || isPortraitDiff || watermarkUrl !== activeTemplate.watermarkUrl;
  }, [activeTemplateId, templates, watermarkSettingsByFormat, watermarkUrl]);

  const {
    images: uploadedImages,
    imagesByFormat,
    isUploading,
    uploadFiles,
    updateImageById,
    removeImageById,
    reorderImages,
    clearImages,
    syncWithFormats,
    unifyImages,
    uploadWatermark,
  } = useImageUpload({ maxImages: MAX_IMAGES, defaultEffect: "zoom-in" });

  /** Intercept format change — warn if images are already uploaded. */
  const handleFormatChange = useCallback(
    (values: string[]) => {
      const next = values as VideoFormat[];
      if (uploadedImages.length > 0) {
        setPendingFormats(next); // show dialog
      } else {
        setFormats(next);
      }
    },
    [uploadedImages.length],
  );

  const confirmFormatChange = useCallback(() => {
    if (pendingFormats) {
      clearImages();
      setFormats(pendingFormats);
      setPendingFormats(null);
    }
  }, [pendingFormats, clearImages]);

  const cancelFormatChange = useCallback(() => {
    setPendingFormats(null);
  }, []);

  const handleWatermarkUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      // Resize watermark to max 480px width before uploading
      const resizedFile = await resizeImageFile(file, 480);

      const previewUrl = URL.createObjectURL(resizedFile);
      setWatermarkPreview(previewUrl);

      // Upload watermark and get URL
      const uploadedUrl = await uploadWatermark(resizedFile);
      if (uploadedUrl) {
        setWatermarkUrl(uploadedUrl);
      }
    }
  };

  const handleRemoveWatermark = () => {
    setWatermarkUrl(null);
    setWatermarkPreview(null);
    const input = document.getElementById(
      "watermark-upload",
    ) as HTMLInputElement;
    if (input) {
      input.value = "";
    }
  };

  const handleSelectTemplate = (template: any | null) => {
    if (!template) {
      setWatermarkPreview(BRANDING_PLACEHOLDER);
      setWatermarkUrl(null);
      setWatermarkSettingsByFormat(DEFAULT_WATERMARK_SETTINGS);
      setActiveTemplateId(null);
      return;
    }
    setWatermarkPreview(template.watermarkUrl || BRANDING_PLACEHOLDER);
    setWatermarkUrl(template.watermarkUrl || "");
    setWatermarkSettingsByFormat({
      "16:9": { ...template.settingsLandscape },
      "9:16": { ...template.settingsPortrait },
    });
    setActiveTemplateId(template.id);
  };



  const isCustomLogo =
    !!watermarkUrl && !templates.some((t) => t.watermarkUrl === watermarkUrl);

  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim()) return;

    try {
      const finalSettings = { ...watermarkSettingsByFormat };

      // Process intro video for each format if title is present
      for (const format of formats) {
        const settings = finalSettings[format];
        if (settings.introTitle && settings.introTitle.trim().length > 0) {
          if (checkIfCaptureNeeded(format, settings)) {
            const urls = await captureAndUploadIntro(format);
            if (urls && urls.length > 0) {
              finalSettings[format] = {
                ...settings,
                introFrameUrls: urls,
              };
            }
          }
        }
      }

      await createTemplateMutation.mutateAsync({
        name: newTemplateName.trim(),
        watermarkUrl: (watermarkUrl === BRANDING_PLACEHOLDER || !watermarkUrl) ? "" : watermarkUrl,
        settingsLandscape: finalSettings["16:9"],
        settingsPortrait: finalSettings["9:16"],
      });

      toast({
        title: t("account.templates.watermarkTemplate.saveSuccess"),
        description: t("account.templates.watermarkTemplate.saveSuccessDesc"),
      });
      setIsSaveTemplateDialogOpen(false);
      setNewTemplateName("");
    } catch (error) {
      toast({
        title: t("account.templates.watermarkTemplate.saveError"),
        description: t("account.templates.watermarkTemplate.saveErrorDesc"),
        variant: "destructive",
      });
    }
  };

  const steps = [
    { id: "general", title: t("createVideo.videoName") },
    ...(formats.includes("16:9")
      ? [
          {
            id: "landscape",
            title: formats.length > 1 && useSplitFormat ? t("createVideo.landscapeUploadTitle") : t("createVideo.imageUploadTitle"),
            format: "16:9" as VideoFormat,
          },
        ]
      : []),
    ...(formats.includes("9:16") && (useSplitFormat || !formats.includes("16:9"))
      ? [
          {
            id: "portrait",
            title: formats.length > 1 && useSplitFormat ? t("createVideo.portraitUploadTitle") : t("createVideo.imageUploadTitle"),
            format: "9:16" as VideoFormat,
          },
        ]
      : []),
    { id: "watermark", title: t("createVideo.watermarkLabel") },
  ];

  const currentStepId = steps[currentStep - 1]?.id || "general";
  const currentFormat = steps[currentStep - 1]?.format;

  const uploadedImagesForStep = currentFormat
    ? imagesByFormat[currentFormat]
    : uploadedImages;

  // Create combined images list with empty slots
  const totalSlots = Math.max(INITIAL_SLOTS, uploadedImagesForStep.length + 1);
  const images: VideoImage[] = [];

  // Add uploaded images first
  uploadedImagesForStep.forEach((img) => {
    images.push({
      id: img.sharedId,
      src: img.url,
      effect: img.effect,
      isTemp: img.isTemp,
      disabled: false,
      mediaType: img.mediaType,
    });
  });

  // Add empty slots (only the next available slot is enabled)
  for (let i = images.length; i < Math.min(totalSlots, MAX_IMAGES); i++) {
    images.push({
      id: `empty-${i}`,
      src: undefined,
      effect: "zoom-in",
      disabled: i > uploadedImagesForStep.length,
    });
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Only allow reordering of uploaded images using sharedId
      const activeIndex = uploadedImagesForStep.findIndex(
        (img) => img.sharedId === active.id,
      );
      const overIndex = uploadedImagesForStep.findIndex(
        (img) => img.sharedId === over.id,
      );

      if (activeIndex !== -1 && overIndex !== -1) {
        reorderImages(activeIndex, overIndex, useSplitFormat ? currentFormat : undefined);
      }
    }
    setActiveId(null);
  };

  const handleEffectChange = (imageId: string, effect: string) => {
    // Check if it's an uploaded image
    if (!imageId.startsWith("empty-")) {
      updateImageById(imageId, { effect: effect as FrontendEffect }, useSplitFormat ? currentFormat : undefined);
    }
  };

  const handleImageUpload = useCallback(
    async (slotId: string, files: File[]) => {
      try {
        const replaceId = slotId.startsWith("empty-") ? undefined : slotId;

        // Track image upload start
        trackImageUpload.start(files.length);

        // If we are in a specific format step AND useSplitFormat is true, only upload for that format
        // Otherwise, upload for all selected formats
        const uploadFormats = (currentFormat && useSplitFormat) ? [currentFormat] : formats;
        await uploadFiles(files, uploadFormats, replaceId);

        // Track image upload completion
        trackImageUpload.complete(files.length);
      } catch (error) {
        // Track image upload error
        trackImageUpload.failed(
          error instanceof Error ? error.message : "Upload failed",
        );
        throw error; // Re-throw to maintain original behavior
      }
    },
    [uploadFiles, formats, currentFormat, useSplitFormat],
  );

  const handleImageRemove = useCallback(
    (sharedId: string) => {
      if (!sharedId.startsWith("empty-")) {
        removeImageById(sharedId, useSplitFormat ? currentFormat : undefined);
      }
    },
    [removeImageById, currentFormat, useSplitFormat],
  );

  // Sync images when formats change
  const lastFormatsRef = useRef<VideoFormat[]>(formats);
  useEffect(() => {
    const formatsChanged = JSON.stringify(lastFormatsRef.current) !== JSON.stringify(formats);
    
    // Only sync if formats actually changed and we are NOT in split mode
    // If we are in split mode, we want assets to remain independent
    if (formatsChanged && !useSplitFormat) {
      syncWithFormats(formats);
    }
    
    lastFormatsRef.current = formats;
  }, [formats, syncWithFormats, useSplitFormat]);

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  };

  const handleSplitFormatToggle = (val: boolean) => {
    if (!val && useSplitFormat && uploadedCount > 0) {
      setIsSplitToggleDialogOpen(true);
    } else {
      setUseSplitFormat(val);
    }
  };

  const confirmSplitFormatToggle = useCallback(() => {
    if (currentFormat) {
      unifyImages(currentFormat);
    }
    setUseSplitFormat(false);
    setIsSplitToggleDialogOpen(false);
  }, [unifyImages, currentFormat]);

  const handleBrandingChoice = useCallback(
    (choice: "yes" | "no") => {
      setWatermarkChoice(choice);
      if (choice === "yes" && !watermarkPreview) {
        setWatermarkPreview(BRANDING_PLACEHOLDER);
      } else if (choice === "no") {
        handleRemoveWatermark();
      }
    },
    [watermarkPreview],
  );

  const prevStep = () => {
    if (currentStepId === "watermark" && watermarkChoice !== null) {
      setWatermarkChoice(null);
      return;
    }
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };



  const captureAndUploadIntro = async (format: VideoFormat): Promise<string[] | null> => {
    const captureRef = captureRefs.current?.[format];
    if (!captureRef) {
      console.warn(`[captureAndUploadIntro] No capture ref for format ${format}`);
      return null;
    }

    setIsCapturing(true);
    try {
      const urls = await captureRef.captureIntroClip();
      return urls;
    } catch (err) {
      console.error("[captureAndUploadIntro] failed:", err);
      toast({
        variant: "destructive",
        title: t("createVideo.captureError") || "Intro Capture Failed",
        description: t("createVideo.captureErrorDesc") || "Could not generate the branding animation. Please try again.",
      });
      return null;
    } finally {
      setIsCapturing(false);
    }
  };

  const checkIfCaptureNeeded = (format: VideoFormat, settings: WatermarkSettings) => {
    if (!settings.introEnabled) return false;
    // Trigger capture if no frames stored yet
    if (!settings.introFrameUrls || settings.introFrameUrls.length === 0) return true;

    const relevantSettings = {
      introEnabled: settings.introEnabled,
      introTitle: settings.introTitle,
      introStyle: settings.introStyle,
      introAnimation: settings.introAnimation,
      introBgColor: settings.introBgColor,
      introBgOpacity: settings.introBgOpacity,
      introTextColor: settings.introTextColor,
      introFont: settings.introFont,
      introAlignment: settings.introAlignment,
      contactCard: settings.contactCard,
    };

    const activeTemplate = templates.find((t) => t.id === activeTemplateId);

    // 1. If it matches the active template settings, frames are valid
    if (activeTemplate) {
      const templateSettings = format === "16:9" ? activeTemplate.settingsLandscape : activeTemplate.settingsPortrait;
      const templateRelevant = {
        introEnabled: templateSettings.introEnabled,
        introTitle: templateSettings.introTitle,
        introStyle: templateSettings.introStyle,
        introAnimation: templateSettings.introAnimation,
        introBgColor: templateSettings.introBgColor,
        introBgOpacity: templateSettings.introBgOpacity,
        introTextColor: templateSettings.introTextColor,
        introFont: templateSettings.introFont,
        introAlignment: templateSettings.introAlignment,
        contactCard: templateSettings.contactCard,
      };

      if (JSON.stringify(relevantSettings) === JSON.stringify(templateRelevant)) {
        return false;
      }
    }

    // 2. If it matches the last submitted settings, frames are valid
    const lastSettings = lastSubmittedSettingsRef.current?.[format];
    if (lastSettings) {
      const lastRelevant = {
        introEnabled: lastSettings.introEnabled,
        introTitle: lastSettings.introTitle,
        introStyle: lastSettings.introStyle,
        introAnimation: lastSettings.introAnimation,
        introBgColor: lastSettings.introBgColor,
        introBgOpacity: lastSettings.introBgOpacity,
        introTextColor: lastSettings.introTextColor,
        introFont: lastSettings.introFont,
        introAlignment: lastSettings.introAlignment,
        contactCard: lastSettings.contactCard,
      };
      if (JSON.stringify(relevantSettings) === JSON.stringify(lastRelevant)) {
        return false;
      }
    }

    // Settings differ from template & last submit — must re-capture
    return true;
  };

  const handlePreSubmitCheck = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (watermarkChoice !== "yes") {
      return handleSubmit();
    }

    const activeTemplate = templates.find((t) => t.id === activeTemplateId);
    
    if (activeTemplate && hasTemplateModifications) {
       setSubmitPromptType("update_template");
       return;
    }
    
    const hasOpeningTitle = (watermarkSettingsByFormat["16:9"]?.introTitle?.trim()?.length || 0) > 0 || 
                            (watermarkSettingsByFormat["9:16"]?.introTitle?.trim()?.length || 0) > 0;
                            
    if (!activeTemplate && (watermarkUrl || hasOpeningTitle)) {
       setSubmitPromptType("new_template");
       return;
    }
    
    return handleSubmit();
  };

  const handleUpdateTemplateAndSubmit = async () => {
    setIsTemplateSavingDialogVisible(true);
    try {
      const finalSettings = { ...watermarkSettingsByFormat };

      // Process intro video for each format if title is present
      for (const format of formats) {
        const settings = finalSettings[format];
        if (settings.introTitle && settings.introTitle.trim().length > 0) {
          if (checkIfCaptureNeeded(format, settings)) {
            const urls = await captureAndUploadIntro(format);
            if (urls && urls.length > 0) {
              finalSettings[format] = {
                ...settings,
                introFrameUrls: urls,
              };
            }
          }
        }
      }

      setWatermarkSettingsByFormat(finalSettings); // Update state so handleSubmit skips capture

      const activeTemplate = templates.find((t) => t.id === activeTemplateId);
      if (activeTemplate) {
        await updateTemplateMutation.mutateAsync({
          id: activeTemplate.id,
          name: activeTemplate.name,
          watermarkUrl: watermarkUrl || "",
          settingsLandscape: finalSettings["16:9"],
          settingsPortrait: finalSettings["9:16"],
        });
        
        toast({
          title: "Template Updated",
          description: "Your branding changes have been saved.",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Update Error",
        description: "Failed to update the template.",
        variant: "destructive",
      });
    } finally {
      setIsTemplateSavingDialogVisible(false);
    }

    setSubmitPromptType(null);
    handleSubmit();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Track video creation start
    trackVideoCreation.start();

    // In unified mode, all selected formats should use the combined image list
    const finalImagesByFormat = { ...imagesByFormat };
    if (!useSplitFormat) {
      formats.forEach((f) => {
        finalImagesByFormat[f] = uploadedImages;
      });
    }

    const finalWatermarkSettings = { ...watermarkSettingsByFormat };

    if (watermarkChoice === "no") {
      for (const format of formats) {
        finalWatermarkSettings[format] = {
          ...finalWatermarkSettings[format],
          logoEnabled: false,
          introEnabled: false,
        };
      }
    } else {
      // Process intro video for each format if title is present
      for (const format of formats) {
        const settings = finalWatermarkSettings[format];
        if (settings.introEnabled && settings.introTitle && settings.introTitle.trim().length > 0) {
          if (checkIfCaptureNeeded(format, settings)) {
            const urls = await captureAndUploadIntro(format);
            if (urls && urls.length > 0) {
              finalWatermarkSettings[format] = {
                ...settings,
                introFrameUrls: urls,
              };
            }
          }
        }
      }
    }

    lastSubmittedSettingsRef.current = { ...finalWatermarkSettings };

    onFormSubmit?.({
      name,
      formats,
      imagesByFormat: finalImagesByFormat,
      watermarkUrl: watermarkUrl === BRANDING_PLACEHOLDER ? "" : watermarkUrl,
      watermarkSettingsByFormat: finalWatermarkSettings,
    });
  };

  const uploadedCount = uploadedImagesForStep.length;
  // In unified mode (useSplitFormat=false) all formats share the same synced image list.
  // Only check the primary image count so a failed sync on a secondary format doesn't
  // permanently block the submit button. In split-format mode each format must meet
  // the minimum independently since they carry different assets.
  const canSubmit = (
    useSplitFormat
      ? formats.every(f => imagesByFormat[f].length >= MIN_IMAGES)
      : uploadedImages.length >= MIN_IMAGES
  ) && !isSubmitting && !isUploading;

  const canGoToStep2 = formats.length > 0;
  const canGoToNextStep = uploadedCount >= MIN_IMAGES && !isUploading;

  return (
    <div
      className={cn("space-y-8 4k:space-y-24 pb-32 lg:pb-0", className)}
      {...props}
    >
      {/* Header & Step Indicators */}
      <div className="space-y-8 2k:space-y-16 4k:space-y-24">
        <div className="space-y-4 2k:space-y-8 4k:space-y-12 md:max-w-[50%]">
          <h1 className="font-h2 text-black">{t("createVideo.title")}</h1>
          <p className="font-body text-black whitespace-pre-wrap">
            {t("createVideo.description")}
          </p>
        </div>

        {/* Progress Tracker */}
        <div className="relative">
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-black/5 -translate-y-1/2" />
          <div className="relative flex justify-between">
            {steps.map((step, index) => {
              const stepNumber = index + 1;
              return (
                <div
                  key={step.id}
                  className="flex flex-col items-center gap-4 bg-lavender px-4 relative z-10"
                >
                  <div
                    className={cn(
                      "w-10 h-10 2k:size-16 4k:size-24 rounded-full flex items-center justify-center font-bold transition-all border-2",
                      currentStep === stepNumber
                        ? "bg-black text-white border-black scale-110 shadow-xl shadow-black/10"
                        : currentStep > stepNumber
                          ? "bg-white text-black border-black"
                          : "bg-white text-muted-foreground border-black/5",
                    )}
                  >
                    {currentStep > stepNumber ? "✓" : stepNumber}
                  </div>
                  <span
                    className={cn(
                      "text-sm 2k:text-2xl 4k:text-4xl font-medium transition-all whitespace-nowrap",
                      currentStep === stepNumber
                        ? "text-black"
                        : "text-muted-foreground",
                    )}
                  >
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <form
        onSubmit={handlePreSubmitCheck}
        className="space-y-8 2k:space-y-14 4k:space-y-16 min-h-[400px]"
      >
        {currentStepId === "general" && (
          <GeneralInfoStep
            name={name}
            setName={setName}
            formats={formats}
            handleFormatChange={handleFormatChange}
            isSubmitting={isSubmitting}
            nextStep={nextStep}
            canGoToStep2={canGoToStep2}
          />
        )}

        {(currentStepId === "landscape" || currentStepId === "portrait") && (
          <MediaUploadStep
            uploadedCount={uploadedCount}
            maxImages={MAX_IMAGES}
            minImages={MIN_IMAGES}
            sensors={sensors}
            images={images}
            handleDragStart={handleDragStart}
            handleDragEnd={handleDragEnd}
            handleEffectChange={handleEffectChange}
            handleImageUpload={handleImageUpload}
            handleImageRemove={handleImageRemove}
            isSubmitting={isSubmitting}
            prevStep={prevStep}
            nextStep={nextStep}
            canGoToStep3={canGoToNextStep}
            format={currentFormat}
            useSplitFormat={useSplitFormat}
            setUseSplitFormat={handleSplitFormatToggle}
            totalSelectedFormats={formats.length}
            // Use format presence (upload steps always carry a `format` prop;
            // general and watermark steps do not) to robustly find the first upload step.
            isFirstUploadStep={currentStepId === steps.find(s => s.format !== undefined)?.id}
          />
        )}

        {/* Confirmation Dialog for switching back to Unified Media */}
        <Dialog open={isSplitToggleDialogOpen} onOpenChange={setIsSplitToggleDialogOpen}>
          <DialogContent className="max-w-md rounded-[32px] p-8 border-none shadow-2xl">
            <DialogHeader className="space-y-4">
              <DialogTitle className="text-2xl font-bold">
                {t("createVideo.splitFormatToggleTitle")}
              </DialogTitle>
              <p className="text-muted-foreground leading-relaxed">
                {t("createVideo.splitFormatToggleDescription")}
              </p>
            </DialogHeader>
            <div className="flex flex-col gap-3 pt-6">
              <button
                type="button"
                onClick={confirmSplitFormatToggle}
                className="w-full py-4 bg-black text-white rounded-2xl font-bold hover:bg-black/90 transition-colors"
              >
                {t("createVideo.splitFormatToggleConfirm")}
              </button>
              <DialogClose asChild>
                <button
                  type="button"
                  className="w-full py-4 bg-black/5 text-black rounded-2xl font-bold hover:bg-black/10 transition-colors"
                >
                  {t("common.cancel")}
                </button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>

        {currentStepId === "watermark" && (
          <WatermarkStep
            watermarkChoice={watermarkChoice}
            setWatermarkChoice={handleBrandingChoice}
            handleRemoveWatermark={handleRemoveWatermark}
            watermarkPreview={watermarkPreview}
            templates={templates}
            handleSelectTemplate={handleSelectTemplate}
            isCustomLogo={isCustomLogo}
            activeTemplateId={activeTemplateId}
            setIsSaveTemplateDialogOpen={setIsSaveTemplateDialogOpen}
            watermarkUrl={watermarkUrl}
            formats={formats}
            watermarkSettingsByFormat={watermarkSettingsByFormat}
            setWatermarkSettingsByFormat={setWatermarkSettingsByFormat}
            getContainerAlignment={getContainerAlignment}
            handleWatermarkUpload={handleWatermarkUpload}
            prevStep={prevStep}
            quota={quota}
            canSubmit={canSubmit}
            isSubmitting={isSubmitting || (isCapturing && !isSavingTemplate)}
            isTemplateUpdating={isSavingTemplate}

            activeFormat={activeBrandingFormat}
            setActiveFormat={setActiveBrandingFormat}
            hasModifications={hasTemplateModifications}
            handleUpdateTemplate={async () => {
              const activeTemplate = templates.find((t) => t.id === activeTemplateId);
              if (!activeTemplate) return;
              setIsSavingTemplate(true);
              setIsTemplateSavingDialogVisible(true);
              try {
                const finalSettings = { ...watermarkSettingsByFormat };
                for (const format of formats) {
                  const settings = finalSettings[format];
                  if (settings.introEnabled && settings.introTitle && settings.introTitle.trim().length > 0) {
                    if (checkIfCaptureNeeded(format, settings)) {
                      const urls = await captureAndUploadIntro(format);
                      if (urls && urls.length > 0) {
                        finalSettings[format] = {
                          ...settings,
                          introFrameUrls: urls,
                        };
                      }
                    }
                  }
                }
                await updateTemplateMutation.mutateAsync({
                  id: activeTemplate.id,
                  name: activeTemplate.name,
                  watermarkUrl: watermarkUrl || "",
                  settingsLandscape: finalSettings["16:9"],
                  settingsPortrait: finalSettings["9:16"],
                });
                setWatermarkSettingsByFormat(finalSettings);
                toast({
                  title: t("account.templates.watermarkTemplate.updateSuccess"),
                  description: t("account.templates.watermarkTemplate.updateSuccessDesc"),
                });
              } catch (error) {
                console.error("[handleUpdateTemplate] error:", error);
                toast({
                  title: t("account.templates.watermarkTemplate.updateError"),
                  description: t("account.templates.watermarkTemplate.updateErrorDesc"),
                  variant: "destructive",
                });
              } finally {
                setIsSavingTemplate(false);
                setIsTemplateSavingDialogVisible(false);
              }
            }}
          />
        )}

        {/* Hidden Background Capture Previews */}
        <div className="fixed left-[-9999px] top-0 pointer-events-none" aria-hidden="true">
          {formats.map(format => (
            <WatermarkPreview
              key={`capture-${format}`}
              captureIntroClipRef={(el: { captureIntroClip: () => Promise<string[]> } | null) => { captureRefs.current![format] = el; }}
              onCapturePhaseChange={handleCapturePhaseChange}
              aspectRatio={format}
              watermarkPreview={watermarkUrl}
              watermarkAlignment={watermarkSettingsByFormat[format].alignment}
              watermarkPadding={watermarkSettingsByFormat[format].padding}
              watermarkOpacity={watermarkSettingsByFormat[format].opacity}
              watermarkSize={watermarkSettingsByFormat[format].size}
              getContainerAlignment={getContainerAlignment}
              introTitle={watermarkSettingsByFormat[format].introTitle}
              introStyle={watermarkSettingsByFormat[format].introStyle as any}
              introAnimation={watermarkSettingsByFormat[format].introAnimation}
              introBgColor={watermarkSettingsByFormat[format].introBgColor}
              introBgOpacity={watermarkSettingsByFormat[format].introBgOpacity}
              introTextColor={watermarkSettingsByFormat[format].introTextColor}
              introFont={watermarkSettingsByFormat[format].introFont}
              introAlignment={watermarkSettingsByFormat[format].introAlignment}
              contactCard={watermarkSettingsByFormat[format].contactCard}
            />
          ))}
        </div>
      </form>

      {/* Format change confirmation dialog */}
      <AlertDialog
        open={pendingFormats !== null}
        onOpenChange={(open) => !open && cancelFormatChange()}
      >
        <AlertDialogContent>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 text-black/40 hover:text-black rounded-full"
            onClick={cancelFormatChange}
          >
            <X className="size-4" />
          </Button>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("createVideo.formatChangeTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("createVideo.formatChangeDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelFormatChange}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmFormatChange}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("createVideo.formatChangeConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pre-Submit Prompts */}
      <AlertDialog
        open={submitPromptType !== null}
        onOpenChange={(open) => {
          if (!open) setSubmitPromptType(null);
        }}
      >
        <AlertDialogContent className="rounded-2xl sm:rounded-[32px] p-8">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-6 top-6 text-black/40 hover:text-black rounded-full"
            onClick={() => setSubmitPromptType(null)}
          >
            <X className="size-5" />
          </Button>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-bold">
              {submitPromptType === "update_template" 
                ? t("createVideo.unsavedTemplateChangesTitle") || "Unsaved Template Changes" 
                : t("createVideo.unsavedCustomBrandingTitle") || "Unsaved Custom Branding"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-black/70">
              {submitPromptType === "update_template" 
                ? t("createVideo.unsavedTemplateChangesDesc") || "You've made changes to this template. Do you want to update it before creating your video?" 
                : t("createVideo.unsavedCustomBrandingDesc") || "You've created a custom branding layout. Would you like to save it as a template for future use?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row-reverse gap-3 mt-8">
            <Button
              variant="outline"
              onClick={() => {
                setSubmitPromptType(null);
                handleSubmit();
              }}
              className="flex-1 h-12 rounded-xl"
            >
              {t("createVideo.justCreateVideo") || "No, just create video"}
            </Button>
            {submitPromptType === "update_template" ? (
              <Button
                onClick={handleUpdateTemplateAndSubmit}
                disabled={updateTemplateMutation.isPending || isCapturing}
                className="flex-1 h-12 rounded-xl gap-2 font-bold bg-black text-white"
              >
                {(updateTemplateMutation.isPending || isCapturing) && <Loader2 className="size-4 animate-spin" />}
                {t("createVideo.updateTemplateAndContinue") || "Update Template & Continue"}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setSubmitPromptType(null);
                  setIsSaveTemplateDialogOpen(true);
                }}
                className="flex-1 h-12 rounded-xl gap-2 font-bold bg-black text-white"
              >
                {t("createVideo.saveAsNewTemplate") || "Save as New Template"}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Template Dialog */}
      <Dialog
        open={isSaveTemplateDialogOpen}
        onOpenChange={setIsSaveTemplateDialogOpen}
      >
        <DialogContent className="sm:max-w-[450px] rounded-[32px] border-none shadow-2xl p-0 overflow-hidden">
          <div className="p-8 space-y-6">
            <DialogHeader className="space-y-2 text-left">
              <DialogTitle className="text-2xl font-bold tracking-tight">
                {t("account.templates.watermarkTemplate.saveTitle")}
              </DialogTitle>
              <p className="text-muted-foreground">
                {t("account.templates.watermarkTemplate.saveHint")}
              </p>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template-name" className="text-sm font-bold ml-1">
                  {t("account.templates.watermarkTemplate.nameLabel")}
                </Label>
                <Input
                  id="template-name"
                  placeholder={t("account.templates.watermarkTemplate.namePlaceholder")}
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  className="h-12 rounded-2xl border-black/5 bg-black/[0.02] focus:bg-white transition-all duration-200 px-4"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsSaveTemplateDialogOpen(false)}
                className="flex-1 h-12 rounded-2xl font-bold hover:bg-black/5"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleSaveTemplate}
                disabled={!newTemplateName.trim() || createTemplateMutation.isPending || isCapturing}
                className="flex-[1.5] h-12 bg-black hover:bg-black/90 text-white rounded-2xl font-bold shadow-lg shadow-black/10 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 gap-2"
              >
                {createTemplateMutation.isPending || isCapturing ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <Save className="size-5" />
                )}
                {t("account.templates.watermarkTemplate.saveButton")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile Sticky Button - Bottom of Screen */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-black/5 p-4 shadow-lg z-1">
        <div className="max-w-7xl mx-auto flex flex-col gap-3">
          {currentStep > 1 && (
            <Button
              type="button"
              onClick={prevStep}
              variant="outline"
              className="w-full rounded-full border-black/10 py-6 font-link cursor-pointer"
            >
              {t("common.back") || "Back"}
            </Button>
          )}
          {currentStep < steps.length ? (
            <Button
              type="button"
              disabled={
                (currentStepId === "general" && !canGoToStep2) ||
                ((currentStepId === "landscape" || currentStepId === "portrait") && !canGoToNextStep)
              }
              onClick={nextStep}
              className="w-full rounded-full bg-black text-white py-6 font-link cursor-pointer"
            >
              {t("common.next") || "Next"}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={!canSubmit || quota?.videosRemaining === 0 || isCapturing}
              onClick={() => handlePreSubmitCheck()}
              className={cn(
                "w-full rounded-full bg-black hover:bg-black/90 text-white text-md px-6 py-6 disabled:opacity-50 cursor-pointer font-link",
              )}
            >
              {isSubmitting || isCapturing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isCapturing
                    ? `${t("createVideo.preparingBranding") || "Preparing..."} (${captureProgress}%)`
                    : t("createVideo.creating")}
                </>
              ) : (
                t("createVideo.createButton")
              )}
            </Button>
          )}

          {/* Quota Info - Mobile */}
          {quota && currentStep === 3 && (
            <p
              className={cn(
                "text-xs text-center font-medium",
                quota.videosRemaining > 0
                  ? "text-muted-foreground"
                  : "text-destructive",
              )}
            >
              {quota.videosRemaining > 0
                ? t("createVideo.videosRemaining", {
                    count: quota.videosRemaining,
                    label:
                      quota.videosRemaining === 1
                        ? t("createVideo.videoSingular")
                        : t("createVideo.videoPlural"),
                  })
                : t("createVideo.quotaExhausted")}
            </p>
          )}
        </div>
      </div>

      {/* Enlarge Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              {t("createVideo.watermarkPreview")}
              <DialogClose asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <X className="h-4 w-4" />
                </Button>
              </DialogClose>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {formats.map((format) => (
              <div key={format} className="space-y-2">
                <h4 className="font-medium text-sm text-muted-foreground">
                  {t("createVideo.formatLabelWithAspect", { aspect: format })}
                </h4>
                <div
                  className={cn(
                    "relative w-full rounded-lg overflow-hidden bg-gray-200 flex",
                    format === "16:9" ? "aspect-video" : "aspect-9/16",
                  )}
                  style={{
                    ...getContainerAlignment(
                      watermarkSettingsByFormat[format].alignment,
                    ),
                    padding: `${watermarkSettingsByFormat[format].padding}%`,
                  }}
                >
                  <img
                    src={
                      format === "16:9"
                        ? placeholderImage
                        : portraitPlaceholderImage
                    }
                    alt="Preview"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <img
                    src={watermarkPreview || ""}
                    alt="Watermark"
                    className="relative"
                    style={{
                      width: `${watermarkSettingsByFormat[format].size}%`,
                      opacity: watermarkSettingsByFormat[format].opacity / 100,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Saving Template Progress Dialog */}
      <AlertDialog open={isTemplateSavingDialogVisible}>
        <AlertDialogContent className="sm:max-w-md flex flex-col items-center justify-center p-12 space-y-6">
          <div className="relative flex items-center justify-center w-16 h-16">
            <div className="absolute w-full h-full border-4 border-black/10 rounded-full" />
            <div className="absolute w-full h-full border-4 border-black border-t-transparent rounded-full animate-spin" />
          </div>
          <AlertDialogTitle className="text-2xl font-medium">{t("createVideo.savingTemplate") || "Saving template"}</AlertDialogTitle>
          <AlertDialogDescription className="text-base text-center">
            {t("createVideo.savingTemplateDesc") || "Please wait while we update your template settings..."}
          </AlertDialogDescription>
        </AlertDialogContent>
      </AlertDialog>

      {/* NOTE: Capturing Progress Dialog commented out since it's simply capture and upload. Will be used/re-enabled later if needed. */}
      {/* 
      <AlertDialog open={isCapturing}>
        <AlertDialogContent className="sm:max-w-md flex flex-col items-center justify-center p-12 space-y-6">
          <div className="relative flex items-center justify-center w-16 h-16">
            <div className="absolute w-full h-full border-4 border-black/10 rounded-full" />
            <div className="absolute w-full h-full border-4 border-black border-t-transparent rounded-full animate-spin" />
          </div>
          <AlertDialogTitle className="text-2xl font-medium text-center">
            {`${t("createVideo.preparingBranding") || "Preparing branding"} (${captureProgress}%)`}
          </AlertDialogTitle>
          <div className="w-full bg-black/10 rounded-full h-2 overflow-hidden">
            <div className="bg-black h-full transition-all duration-300" style={{ width: `${captureProgress}%` }} />
          </div>
          <AlertDialogDescription className="text-base text-center">
            {t("createVideo.pleaseWait") || "Please wait while we process the branding animation. Do not close this page."}
          </AlertDialogDescription>
        </AlertDialogContent>
      </AlertDialog>
      */}
    </div>
  );
}

export { CreateVideoForm };
